import {
  SORT_COLUMNS,
  SORT_DIRECTION,
} from "@/pages/requests/components/users/components/constants";
import { SortDirectionsProps } from "@/pages/requests/components/users/components/types";
import { isArray, orderBy, toString } from "lodash";
import { useCallback, useState } from "react";
import { Person } from "@/services";

export const useSort = (
  data: Person[] | null,
  updateData: (data: Person[]) => void,
) => {
  const [searchValue, setSearchValue] = useState("");

  const [sortDirections, setSortDirections] = useState<SortDirectionsProps>({
    [SORT_COLUMNS.NAME_EN]: SORT_DIRECTION.ASC,
    [SORT_COLUMNS.NAME_AR]: SORT_DIRECTION.ASC,
    [SORT_COLUMNS.NATIONALITY]: SORT_DIRECTION.ASC,
    [SORT_COLUMNS.JOB_TITLE_EN]: SORT_DIRECTION.ASC,
    [SORT_COLUMNS.STATUS]: SORT_DIRECTION.ASC,
  });

  const [appliedFilters, setAppliedFilters] = useState({
    [SORT_COLUMNS.NATIONALITY]: "",
    [SORT_COLUMNS.JOB_TITLE_EN]: "",
    [SORT_COLUMNS.STATUS]: "",
  });

  const [selectedOptions, setSelectedOptions] = useState<Person[]>([]);

  const handleSort = useCallback(
    (columnName: SORT_COLUMNS) => {
      const newSortDirections = { ...sortDirections };

      newSortDirections[columnName] =
        sortDirections[columnName] === SORT_DIRECTION.ASC
          ? SORT_DIRECTION.DESC
          : SORT_DIRECTION.ASC;
      setSortDirections(newSortDirections);

      const sortedData = orderBy(
        data,
        [columnName],
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        [newSortDirections[columnName as keyof typeof SORT_COLUMNS]],
      );

      updateData(sortedData);
    },
    [data, sortDirections, updateData],
  );

  const handleSearch = useCallback(
    (value: string) => {
      setSearchValue(value);
    },
    [setSearchValue],
  );

  const handleApplyFilter = (
    status: boolean,
    id: SORT_COLUMNS,
    selectedOptions: Person[],
  ) => {
    setAppliedFilters(prevFilters => ({
      ...prevFilters,
      [id]: status
        ? selectedOptions.map(option => toString(option[id])).join(",")
        : "",
    }));
  };

  const handleFilterChange = (item: Person) => {
    const isAlreadySelected = selectedOptions.some(
      option => option.Id === item.Id,
    );

    if (isAlreadySelected) {
      setSelectedOptions(
        selectedOptions.filter(option => option.Id !== item.Id),
      );
    } else {
      setSelectedOptions([...selectedOptions, item]);
    }
  };

  const filteredData =
    data && isArray(data)
      ? data?.filter((user: Person) => {
          return (
            (user?.WorkerFullNameEnglish?.toLowerCase().includes(
              searchValue.toLowerCase(),
            ) ||
              user?.WorkerEmiratesId?.toLowerCase().includes(
                searchValue.toLowerCase(),
              ) ||
              user?.WorkerPassportNumber?.toLowerCase().includes(
                searchValue.toLowerCase(),
              )) &&
            (appliedFilters[SORT_COLUMNS.NATIONALITY]
              ? appliedFilters[SORT_COLUMNS.NATIONALITY].includes(
                  user[SORT_COLUMNS.NATIONALITY],
                )
              : true) &&
            (appliedFilters[SORT_COLUMNS.JOB_TITLE_EN]
              ? appliedFilters[SORT_COLUMNS.JOB_TITLE_EN].includes(
                  user[SORT_COLUMNS.JOB_TITLE_EN],
                )
              : true) &&
            (appliedFilters[SORT_COLUMNS.STATUS]
              ? appliedFilters[SORT_COLUMNS.STATUS].includes(
                  String(user[SORT_COLUMNS.STATUS]),
                )
              : true)
          );
        })
      : data;

  return {
    filteredData,
    selectedOptions,
    sortDirections,
    searchValue,
    appliedFilters,
    handleSort,
    handleSearch,
    handleFilterChange,
    handleApplyFilter,
  };
};
